/**
 * Filter Validator Utility
 * 
 * Validates filter syntax for modelFilter and optionFilter expressions
 * before saving them to the database. Filters are evaluated at runtime
 * using eval() in StockingSelection.js, so we test them with mock data.
 */

/**
 * Validates filter syntax by building a test expression with mock values
 * 
 * @param {string} filter - The filter expression to validate
 * @param {string} filterType - Either 'model' or 'option' (determines available variables)
 * @returns {Object} { isValid: boolean, error: string | null }
 */
export const validateFilterSyntax = (filter, filterType = 'model') => {
    // Empty or null filters are valid (they return true at runtime)
    if (!filter || filter.trim() === '') {
        return { isValid: true, error: null };
    }

    try {
        // Mock values for testing (matching runtime normalization logic)
        const mockValues = {
            // Available in both model and option filters
            lengthType: 'ag',
            compressionClass: 'ccl2',
            knitting: 'circular',
            vendorTableId: 'bauerfeind-venotrain',
        };

        // Additional variables available only for option filters
        if (filterType === 'option') {
            Object.assign(mockValues, {
                lengthSize: 'short',
                size: 'm',
                bandType: 'tricotband',
                toeOption: 'open',
                footLength: 'short',
                bandWidth: 'normal',
                ankle: '21-23',
                color: 'black',
                design: 'plain',
                fabric: 'cotton',
                leftright: 'left',
            });
        }

        // Normalize function (strips spaces, converts to lowercase - matches runtime logic)
        const norm = (v) => (v || '').replace(/\s+/g, '').toLowerCase();

        // Build expression similar to runtime evaluation
        let expression = filter.replace(/[{}]/g, '').trim();

        // Replace variables with mock values (case-insensitive)
        Object.keys(mockValues).forEach(key => {
            const regex = new RegExp(key, 'gi');
            expression = expression.replace(regex, `'${norm(mockValues[key])}'`);
        });

        // Normalize all string literals
        expression = expression.replace(/'([^']*)'/g, (_, s) => `'${norm(s)}'`);

        // Add vendorTableId comparison (always appended at runtime)
        expression += ` && '${norm(mockValues.vendorTableId)}' === '${norm(mockValues.vendorTableId)}'`;

        // Test evaluation
        // eslint-disable-next-line no-eval
        const result = eval(expression);

        // Result should be boolean
        if (typeof result !== 'boolean') {
            return {
                isValid: false,
                error: `Expression must evaluate to boolean, got ${typeof result}`,
            };
        }

        return { isValid: true, error: null };
    } catch (error) {
        // Return the error message to help with debugging
        return {
            isValid: false,
            error: error.message || 'Invalid syntax',
        };
    }
};

/**
 * Get available variables for a filter type
 * 
 * @param {string} filterType - Either 'model' or 'option'
 * @returns {Array<string>} List of available variable names
 */
export const getAvailableVariables = (filterType = 'model') => {
    const modelVariables = [
        'lengthType',
        'compressionClass',
        'knitting',
        'vendorTableId',
    ];

    const optionVariables = [
        ...modelVariables,
        'lengthSize',
        'size',
        'bandType',
        'toeOption',
        'footLength',
        'bandWidth',
        'ankle',
        'color',
        'design',
        'fabric',
        'leftright',
    ];

    return filterType === 'option' ? optionVariables : modelVariables;
};

/**
 * Get example filters for a filter type
 * 
 * @param {string} filterType - Either 'model' or 'option'
 * @returns {Array<string>} List of example filter expressions
 */
export const getExampleFilters = (filterType = 'model') => {
    const modelExamples = [
        "lengthType == 'AG'",
        "lengthType == 'AD' || lengthType == 'AG'",
        "compressionClass == 'CCL2' && knitting == 'Circular'",
    ];

    const optionExamples = [
        "toeOption == 'Open'",
        "size == 'M' || size == 'L'",
        "bandType == 'Tricot Band' && color == 'Black'",
    ];

    return filterType === 'option' ? optionExamples : modelExamples;
};
